import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { ChatMessage } from '@features/chat/components/ChatMessage';
import { Logo, RatingButtons } from '@core/components/ui';
import { formatDateCompact } from '@core/components/ui/utils/formatters';
import {
  useRateMessageMutation,
  useAnalyzeMessageRatingMutation,
  useMarkAnalysisReviewMutation,
} from '../api/conversationsApi';

export function MessageList({ messages, sessionId }) {
  const { t } = useTranslation();
  const [rateMessage] = useRateMessageMutation();
  const [analyzeMessageRating] = useAnalyzeMessageRatingMutation();
  const [markAnalysisReview] = useMarkAnalysisReviewMutation();

  // Transform messages to match ChatMessage component format
  const transformMessage = (msg) => ({
    question: msg.content,
    answer: typeof msg.answer === 'string' ? msg.answer : msg.answer?.content || msg.answer?.text,
    response: typeof msg.answer === 'string' ? msg.answer : msg.answer?.content || msg.answer?.text,
    citations: typeof msg.answer === 'object' ? msg.answer?.citations : null,
    created_at: msg.created_at,
  });

  // Render user-only bubble for messages without a bot answer (e.g. needs_attention)
  const renderUserOnlyMessage = (message) => (
    <div className="p-2 animate-fadeIn">
      <div className="flex gap-2 sm:gap-3">
        <div className="size-6 sm:size-8 rounded-full shrink-0 bg-brand/10 flex items-center justify-center">
          <Icon icon="lucide:user" className="size-3.5 sm:size-4 text-brand" />
        </div>
        <div className="flex-1">
          <div className="bg-brand/6 text-brand-dark rounded-xl px-3 py-2 sm:px-4 sm:py-3">
            <p className="text-sm break-words">{message.content}</p>
          </div>
          {message.created_at && (
            <p className="text-xs text-muted mt-2">
              {formatDateCompact(message.created_at)}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <div key={message.id}>
          {message.is_admin_message ? (
            // Admin message — bot avatar, orange bubble, with "Answered by" attribution
            <div className="p-2 animate-fadeIn">
              <div className="flex gap-2 sm:gap-3">
                <Logo
                  variant="compact"
                  className="size-6 sm:size-8 rounded-full shrink-0 ml-[0.14rem] text-brand"
                />
                <div className="flex-1">
                  <div className="bg-warning/10 border border-warning/20 rounded-xl px-3 py-2 sm:px-4 sm:py-3">
                    <p className="text-sm text-primary-dark break-words">{message.answer?.content || message.content}</p>
                    <p className="text-[10px] text-primary-dark text-right mt-1">
                      {t('conversations.answeredBy', 'Answered by {{name}}', { name: message.sender_name || t('conversations.adminLabel') })}
                    </p>
                  </div>
                  {message.created_at && (
                    <p className="text-xs text-muted mt-2">
                      {formatDateCompact(message.created_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : message.content && !message.answer ? (
            // User message without bot response — render user bubble only
            renderUserOnlyMessage(message)
          ) : (
            <>
              {/* Reuse existing chat message component */}
              <ChatMessage
                message={transformMessage(message)}
                showTimestamp={true}
                enableTypewriter={false}
              />

              {/* Rating Buttons below each bot response */}
              {message.answer && (
                <div className="ml-11 mt-2">
                  <RatingButtons
                    messageId={message.id}
                    sessionId={sessionId}
                    existingRatings={message.ratings || []}
                    onRate={(params) => rateMessage(params).unwrap()}
                    onAnalyze={(params) => analyzeMessageRating({ ...params, sessionId }).unwrap()}
                    onMarkReview={(params) => markAnalysisReview({ ...params, sessionId }).unwrap()}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

MessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      answer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]),
      created_at: PropTypes.string.isRequired,
      ratings: PropTypes.array,
      is_admin_message: PropTypes.bool,
      sender_name: PropTypes.string,
    })
  ).isRequired,
  sessionId: PropTypes.string.isRequired,
};
